export interface AnnualOrderedCapacityQuery {
  id: string;
  name: string;
  year: number;
  counterpartyId: string;
  counterpartyName: string;
  contractId: string;
  contractName: string;
  date: string;
  period: string;
  orderedCapacityType: string;
  annualVolume: number;
}

export interface GetAnnualOrderedCapacitiesQueryPayload {
  year: number;
  search?: string;
  numericFilter?: {
    operation: number;
    value?: number;
    range?: {
      min: number;
      max: number;
    };
  };
  orderedCapacityTypeIds?: string[];
  counterpartyIds?: string[];
}

export enum AnnualOrderedCapacityType {
  OrderedByDefault = '02fd3521-d308-4099-9df1-642b62d17d49', // Замовлена(за замовчуванням)
  NewCounterparty = '4ad61a90-343b-45e6-af2f-0dac8e6573e0', // Заява.Новий контрагент
  NewMeteringPoint = 'ce0d505e-fbf0-480d-9829-9b41a92646fb', // Заява.Нова точка обліку
  FirstAdjustmentOfCalculatedByDefault = '317207b9-f666-4d80-b554-65224885ba97', // Коригування розрахованої потужності.Перше(1.10 - 20.10)
  FirstAdjustmentOfNewCounterparty = '56dbad76-e1dc-40f8-b1c7-811c63bea101', // Коригування замовленої потужності.Перше по контрагенту(1.10 - 20.10)
  FirstAdjustmentOfNewMeteringPoint = 'd557c563-5c16-4afc-8e76-af33c55db357', // Коригування замовленої потужності.Перше по точці(1.10 - 20.10)
  SecondAdjustmentOfCalculatedByDefault = '8e5b61fd-91f1-44af-ac2e-6feb34793c81', // Коригування розрахованої потужності.Друге(10.07 - 15.09)
  SecondAdjustmentOfNewCounterparty = '38948d98-f5cd-423b-b163-803c2c6f4c18', // Коригування замовленої потужності.Друге по контрагенту(10.07 - 15.09)
  SecondAdjustmentOfNewMeteringPoint = '406ffb53-6b07-4454-85c9-059f0509d468', // Коригування замовленої потужності.Друге по точці(10.07 - 15.09)
  AdjustmentInitiatedByOrganization = 'c2596d31-3d06-4557-af9b-759d5599e7b9', // Коригування за ініціативою ОГРМ
  CloseContract = '587558ab-ded2-4e7d-9a6a-ee557e783827', // Закриття договору
  CalculatedByDefault = '78b54e6e-e2d1-481d-af9c-0c43d327f920', // Розрахована ОГРМ(за замовчуванням)
  AdjustmentOfNewCounterpartyDeleteMeteringPointFromContract = '650f7941-0e82-4ba4-9ef9-45daf006f94d', // Коригування по контрагенту. Вилучення точки обліку з договору
  AdjustmentOfNewMeteringPointDeleteMeteringPointFromContract = '247eaf2e-debd-4067-acdd-d444d46267bc', // Коригування по точці. Вилучення точки обліку з договору
}

export const AnnualOrderedCapacityName = {
  [AnnualOrderedCapacityType.OrderedByDefault]: 'Замовлена(за замовчуванням)',
  [AnnualOrderedCapacityType.NewCounterparty]: 'Заява.Новий контрагент',
  [AnnualOrderedCapacityType.NewMeteringPoint]: 'Заява.Нова точка обліку',
  [AnnualOrderedCapacityType.FirstAdjustmentOfCalculatedByDefault]:
    'Перше коригування',
  [AnnualOrderedCapacityType.FirstAdjustmentOfNewCounterparty]:
    'Перше коригування',
  [AnnualOrderedCapacityType.FirstAdjustmentOfNewMeteringPoint]:
    'Перше коригування',
  [AnnualOrderedCapacityType.SecondAdjustmentOfCalculatedByDefault]:
    'Друге коригування',
  [AnnualOrderedCapacityType.SecondAdjustmentOfNewCounterparty]:
    'Друге коригування',
  [AnnualOrderedCapacityType.SecondAdjustmentOfNewMeteringPoint]:
    'Друге коригування',
  [AnnualOrderedCapacityType.AdjustmentInitiatedByOrganization]:
    'Коригування за ініціативою ОГРМ',
  [AnnualOrderedCapacityType.CloseContract]: 'Закриття договору',
  [AnnualOrderedCapacityType.CalculatedByDefault]:
    'Розрахована ОГРМ (за замовчуванням)',
  [AnnualOrderedCapacityType.AdjustmentOfNewCounterpartyDeleteMeteringPointFromContract]:
    'Вилучення точки обліку з договору',
  [AnnualOrderedCapacityType.AdjustmentOfNewMeteringPointDeleteMeteringPointFromContract]:
    'Вилучення точки обліку з договору',
};

export const NewApplicationTypesArr = [
  AnnualOrderedCapacityType.NewCounterparty,
  AnnualOrderedCapacityType.NewMeteringPoint,
  AnnualOrderedCapacityType.OrderedByDefault,
];

export interface CapacityType {
  id: string;
  name: string;
}

export type AnnualOrderedCapacityRecord = {
  commercialMeteringPointName: string;
  commercialMeteringPointId: string;
  january: number;
  february: number;
  march: number;
  april: number;
  may: number;
  june: number;
  july: number;
  august: number;
  september: number;
  october: number;
  november: number;
  december: number;
  annualVolume: number;
};

export interface AnnualOrderedCapacityForm {
  id?: string;
  orderedCapacityTypeId: AnnualOrderedCapacityType;
  counterparty: { id: string; name: string } | null;
  contractId: string;
  date: string;
  year: string;
  startPeriod: string;
  endPeriod: string;
  items: { id: string; name?: string; annualVolume?: number }[];
}

export type AnnualOrderedCapacity = {
  id: string;
  name: string;
  orderedCapacityType: string;
  orderedCapacityTypeId: AnnualOrderedCapacityType;
  year: number;
  counterpartyId: string;
  counterpartyName: string;
  contractId: string;
  contractName: string;
  date: string;
  period: string;
  periodStart: number;
  periodEnd: number;
  records: AnnualOrderedCapacityRecord[];
  annualVolume: number;
  originalOrderedCapacityId: string | null;
  allowedAdjustmentTypes: { id: string; name: string }[];
  isModifyAllowed?: boolean;
};

export type CalculateAnnualOrderedCapacityFiler = {
  periodStart: number;
  periodEnd: number;
  year: number;
  counterpartyId: string;
  contractId: string;
};

export type AnnualOrderedCapacityCalculateByContractFiler = {
  year: number;
  contractId: string;
};

export type AnnualOrderedCapacityPayloadItem = {
  commercialMeteringPointId: string;
  annualVolume: number;
};

export type AnnualOrderedCapacityPayload = {
  orderedCapacityTypeId: string;
  periodStart: number;
  periodEnd: number;
  date: string;
  year: number;
  counterpartyId: string;
  contractId: string;
  items: AnnualOrderedCapacityPayloadItem[];
};

export type CalculateAnnualOrderedCapacityByOrganizationFilter = {
  year: number;
};

export type AnnualOrderedCapacityCounterparty = {
  id: string;
  name: string;
};

export type AnnualOrderedCapacityCounterpartiesParams = {
  year: number;
};

export type AnnualOrderedCapacityUpdateByApplicationPayload = {
  id: string;
  date?: string;
  year?: number;
  periodStart?: number;
  periodEnd?: number;
  items?: AnnualOrderedCapacityPayloadItem[];
};

export type AnnualOrderedCapacityQueryItemGrid = {
  id: string;
  name: string;
  year: number;
  counterpartyId: string;
  counterpartyName: string;
  contractId: string;
  contractName: string;
  date: string;
  period: string;
  orderedCapacityType: string;
  orderedCapacityTypeId: string;
  annualVolume: number;
};

export type AnnualOrderedCapacityQueryGrid = {
  isAutoCalculationRunning: true;
  items: AnnualOrderedCapacityQueryItemGrid[];
};
